import { useForm } from '@refinedev/antd';
import { Create } from '@refinedev/antd';
import {
  Form,
  Input,
  InputNumber,
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Spin,
  Button,
  Alert,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { apiClient, tokenManager } from '../../lib/api-client';

interface Region {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
}

interface City {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  regionId: number;
}

interface BusCompany {
  id: number;
  name: string;
}

interface BusStation {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  address?: string;
  cityId: number;
}

interface BusLayout {
  id: number;
  name: string;
  totalSeats: number;
  description?: string;
}

export const BusTripCreate = () => {
  const [form] = Form.useForm();
  const { formProps, saveButtonProps } = useForm({
    resource: 'admin/bus/trips',
    redirect: 'list',
  });

  // Check if current user is a bus company
  const user = tokenManager.getUser();
  const isBusCompany = user?.userType === 'bus_company';
  const busCompanyId = user?.busCompanyId;

  // Store original onFinish from formProps
  const originalOnFinish = formProps.onFinish;

  // Handle form submission with date conversion
  const handleFormSubmit = async (values: Record<string, unknown>) => {
    // Convert dayjs objects to ISO strings for the backend, and strip the
    // UI-only `regionId` from each stop (backend uses forbidNonWhitelisted
    // and would otherwise 400).
    const rawStops = (values.stops as
      | Array<{
          regionId?: number;
          cityId?: number;
          stationId?: number;
          scheduledTime?: { toISOString: () => string };
        }>
      | undefined) ?? [];
    const cleanedStops = rawStops
      .filter((s) => s && s.cityId != null && s.scheduledTime)
      .map((s) => ({
        cityId: s.cityId!,
        ...(s.stationId != null ? { stationId: s.stationId } : {}),
        scheduledTime: s.scheduledTime!.toISOString(),
      }));

    const formattedValues = {
      ...values,
      departureTime: values.departureTime
        ? (values.departureTime as { toISOString: () => string }).toISOString()
        : undefined,
      arrivalTime: values.arrivalTime
        ? (values.arrivalTime as { toISOString: () => string }).toISOString()
        : undefined,
      ...(cleanedStops.length > 0
        ? { stops: cleanedStops }
        : { stops: undefined }),
    };

    // Call the original onFinish with formatted values
    if (originalOnFinish) {
      return originalOnFinish(formattedValues);
    }
  };

  // Custom save button props that manually submits our form
  const customSaveButtonProps = {
    ...saveButtonProps,
    onClick: () => {
      form.submit();
    },
  };

  const [regions, setRegions] = useState<Region[]>([]);
  const [companies, setCompanies] = useState<BusCompany[]>([]);
  const [layouts, setLayouts] = useState<BusLayout[]>([]);
  const [departureCities, setDepartureCities] = useState<City[]>([]);
  const [arrivalCities, setArrivalCities] = useState<City[]>([]);
  const [departureStations, setDepartureStations] = useState<BusStation[]>([]);
  const [arrivalStations, setArrivalStations] = useState<BusStation[]>([]);
  // Used by the stops Form.List so each row can pick any viloyat/shahar/avtovokzal
  // without per-row async fetches.
  const [allCities, setAllCities] = useState<City[]>([]);
  const [allStations, setAllStations] = useState<BusStation[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingLayouts, setLoadingLayouts] = useState(true);
  const [loadingDepartureCities, setLoadingDepartureCities] = useState(false);
  const [loadingArrivalCities, setLoadingArrivalCities] = useState(false);
  const [loadingDepartureStations, setLoadingDepartureStations] = useState(false);
  const [loadingArrivalStations, setLoadingArrivalStations] = useState(false);

  // Fetch regions on mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await apiClient.get('/admin/regions');
        setRegions(response.data);
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, []);

  // Fetch all cities + stations once for the multi-stop pickers.
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [citiesRes, stationsRes] = await Promise.all([
          apiClient.get('/admin/cities'),
          apiClient.get('/admin/bus/stations'),
        ]);
        setAllCities(Array.isArray(citiesRes.data) ? citiesRes.data : []);
        setAllStations(Array.isArray(stationsRes.data) ? stationsRes.data : []);
      } catch (error) {
        console.error('Failed to fetch cities/stations:', error);
      }
    };
    fetchAll();
  }, []);

  // Fetch companies on mount (skip for bus company users)
  useEffect(() => {
    if (isBusCompany) {
      // Auto-set busCompanyId for bus company users
      form.setFieldsValue({ busCompanyId });
      setLoadingCompanies(false);
      return;
    }
    const fetchCompanies = async () => {
      try {
        const response = await apiClient.get('/admin/bus/companies');
        setCompanies(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch bus layouts on mount
  useEffect(() => {
    const fetchLayouts = async () => {
      try {
        const response = await apiClient.get('/admin/bus/layouts');
        // Filter only active layouts
        const activeLayouts = (response.data || []).filter((l: BusLayout & { isActive?: boolean }) => l.isActive !== false);
        setLayouts(activeLayouts);
      } catch (error) {
        console.error('Failed to fetch layouts:', error);
      } finally {
        setLoadingLayouts(false);
      }
    };
    fetchLayouts();
  }, []);

  // Layout drives totalSeats — the backend now reads it from the layout,
  // so the field is gone from the form. Keep the hook around as a no-op
  // for backwards-compat with downstream handlers that pass it.
  const handleLayoutChange = (_layoutId: number | undefined) => {
    // No-op: server reads totalSeats from busLayout when creating.
  };

  // Fetch cities when departure region changes
  const handleDepartureRegionChange = async (regionId: number) => {
    form.setFieldsValue({ departureCityId: undefined, departureBusStationId: undefined });
    setDepartureCities([]);
    setDepartureStations([]);
    if (regionId) {
      setLoadingDepartureCities(true);
      try {
        const response = await apiClient.get(`/admin/cities?regionId=${regionId}`);
        setDepartureCities(response.data);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      } finally {
        setLoadingDepartureCities(false);
      }
    }
  };

  // Fetch cities when arrival region changes
  const handleArrivalRegionChange = async (regionId: number) => {
    form.setFieldsValue({ arrivalCityId: undefined, arrivalBusStationId: undefined });
    setArrivalCities([]);
    setArrivalStations([]);
    if (regionId) {
      setLoadingArrivalCities(true);
      try {
        const response = await apiClient.get(`/admin/cities?regionId=${regionId}`);
        setArrivalCities(response.data);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      } finally {
        setLoadingArrivalCities(false);
      }
    }
  };

  // Fetch bus stations when departure city changes
  const handleDepartureCityChange = async (cityId: number) => {
    form.setFieldsValue({ departureBusStationId: undefined });
    setDepartureStations([]);
    if (cityId) {
      setLoadingDepartureStations(true);
      try {
        const response = await apiClient.get(`/admin/bus/stations?cityId=${cityId}`);
        setDepartureStations(response.data);
      } catch (error) {
        console.error('Failed to fetch bus stations:', error);
      } finally {
        setLoadingDepartureStations(false);
      }
    }
  };

  // Fetch bus stations when arrival city changes
  const handleArrivalCityChange = async (cityId: number) => {
    form.setFieldsValue({ arrivalBusStationId: undefined });
    setArrivalStations([]);
    if (cityId) {
      setLoadingArrivalStations(true);
      try {
        const response = await apiClient.get(`/admin/bus/stations?cityId=${cityId}`);
        setArrivalStations(response.data);
      } catch (error) {
        console.error('Failed to fetch bus stations:', error);
      } finally {
        setLoadingArrivalStations(false);
      }
    }
  };

  return (
    <Create saveButtonProps={customSaveButtonProps} title="Yangi avtobus reysi">
      <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card title="Asosiy ma'lumotlar">
              {isBusCompany ? (
                <Form.Item name="busCompanyId" hidden>
                  <Input type="hidden" />
                </Form.Item>
              ) : (
                <Form.Item
                  label="Avtobus kompaniyasi"
                  name="busCompanyId"
                  rules={[{ required: true, message: 'Kompaniyani tanlang' }]}
                >
                  <Select
                    placeholder="Kompaniyani tanlang"
                    showSearch
                    loading={loadingCompanies}
                    options={companies.map((c) => ({ value: c.id, label: c.name }))}
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                label="Avtobus turi"
                name="busType"
                rules={[{ required: true, message: 'Turni tanlang' }]}
                initialValue="STANDARD"
              >
                <Select
                  options={[
                    { value: 'STANDARD', label: 'Standart' },
                    { value: 'COMFORT', label: 'Komfort' },
                    { value: 'VIP', label: 'VIP' },
                  ]}
                />
              </Form.Item>

              <Form.Item
                label="Avtobus sxemasi"
                name="busLayoutId"
                tooltip="Jami o'rindiqlar soni sxemadan olinadi — alohida kiritish shart emas"
                rules={[{ required: true, message: 'Sxemani tanlang' }]}
              >
                <Select
                  placeholder="Sxemani tanlang"
                  showSearch
                  loading={loadingLayouts}
                  onChange={handleLayoutChange}
                  options={layouts.map((l) => ({
                    value: l.id,
                    label: `${l.name} (${l.totalSeats} o'rindiq)`,
                  }))}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item label="Avtobus raqami" name="busNumber">
                <Input placeholder="01A123AA" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Haydovchi F.I.Sh." name="driverName">
                    <Input placeholder="Akramov Sardor" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Haydovchi telefoni" name="driverPhone">
                    <Input placeholder="+998901234567" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Standart narx (so'm)"
                name="pricePerSeat"
                rules={[{ required: true, message: 'Narxni kiriting' }]}
                tooltip="Bir to'xtashli reys uchun standart narx. Ko'p to'xtashli reyslarda segment narxlari ustun bo'ladi."
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                  }
                  parser={(value) => value!.replace(/\s/g, '') as unknown as 0}
                />
              </Form.Item>
            </Card>

            <Card title="Qo'shimcha" style={{ marginTop: 24 }}>
              <Form.Item label="Tavsif" name="description">
                <Input.TextArea placeholder="Reys haqida qo'shimcha ma'lumot" rows={3} />
              </Form.Item>

              <Form.Item label="Qulayliklar" name="amenities">
                <Select
                  mode="multiple"
                  placeholder="Qulayliklarni tanlang"
                  options={[
                    { value: 'wifi', label: 'Wi-Fi' },
                    { value: 'ac', label: 'Konditsioner' },
                    { value: 'toilet', label: 'Hojatxona' },
                    { value: 'tv', label: 'TV' },
                    { value: 'usb', label: 'USB quvvatlash' },
                    { value: 'water', label: 'Bepul suv' },
                  ]}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Jo'nash joyi">
              <Form.Item
                label="Viloyat"
                name="departureRegionId"
                rules={[{ required: true, message: 'Viloyatni tanlang' }]}
              >
                <Select
                  placeholder="Viloyatni tanlang"
                  showSearch
                  loading={loadingRegions}
                  options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
                  onChange={handleDepartureRegionChange}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                label="Shahar"
                name="departureCityId"
                rules={[{ required: true, message: 'Shaharni tanlang' }]}
              >
                <Select
                  placeholder={departureCities.length === 0 ? "Avval viloyatni tanlang" : "Shaharni tanlang"}
                  showSearch
                  loading={loadingDepartureCities}
                  disabled={departureCities.length === 0}
                  options={departureCities.map((c) => ({ value: c.id, label: c.nameUz }))}
                  onChange={handleDepartureCityChange}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingDepartureCities ? <Spin size="small" /> : 'Shahar topilmadi'}
                />
              </Form.Item>

              <Form.Item
                label="Avtovokzal"
                name="departureBusStationId"
                rules={[{ required: true, message: 'Avtovokzalni tanlang' }]}
              >
                <Select
                  placeholder={departureStations.length === 0 ? "Avval shaharni tanlang" : "Avtovokzalni tanlang"}
                  showSearch
                  loading={loadingDepartureStations}
                  disabled={departureStations.length === 0}
                  options={departureStations.map((s) => ({
                    value: s.id,
                    label: s.nameUz,
                    title: s.address,
                  }))}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingDepartureStations ? <Spin size="small" /> : 'Avtovokzal topilmadi'}
                />
              </Form.Item>

              <Form.Item
                label="Jo'nash vaqti"
                name="departureTime"
                rules={[{ required: true, message: "Jo'nash vaqtini tanlang" }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Sana va vaqtni tanlang"
                />
              </Form.Item>
            </Card>

            <Card title="Yetib borish joyi" style={{ marginTop: 24 }}>
              <Form.Item
                label="Viloyat"
                name="arrivalRegionId"
                rules={[{ required: true, message: 'Viloyatni tanlang' }]}
              >
                <Select
                  placeholder="Viloyatni tanlang"
                  showSearch
                  loading={loadingRegions}
                  options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
                  onChange={handleArrivalRegionChange}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                label="Shahar"
                name="arrivalCityId"
                rules={[{ required: true, message: 'Shaharni tanlang' }]}
              >
                <Select
                  placeholder={arrivalCities.length === 0 ? "Avval viloyatni tanlang" : "Shaharni tanlang"}
                  showSearch
                  loading={loadingArrivalCities}
                  disabled={arrivalCities.length === 0}
                  options={arrivalCities.map((c) => ({ value: c.id, label: c.nameUz }))}
                  onChange={handleArrivalCityChange}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingArrivalCities ? <Spin size="small" /> : 'Shahar topilmadi'}
                />
              </Form.Item>

              <Form.Item
                label="Avtovokzal"
                name="arrivalBusStationId"
                rules={[{ required: true, message: 'Avtovokzalni tanlang' }]}
              >
                <Select
                  placeholder={arrivalStations.length === 0 ? "Avval shaharni tanlang" : "Avtovokzalni tanlang"}
                  showSearch
                  loading={loadingArrivalStations}
                  disabled={arrivalStations.length === 0}
                  options={arrivalStations.map((s) => ({
                    value: s.id,
                    label: s.nameUz,
                    title: s.address,
                  }))}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingArrivalStations ? <Spin size="small" /> : 'Avtovokzal topilmadi'}
                />
              </Form.Item>

              <Form.Item
                label="Yetib borish vaqti"
                name="arrivalTime"
                rules={[{ required: true, message: 'Yetib borish vaqtini tanlang' }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Sana va vaqtni tanlang"
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Card title="Ko'p to'xtashlar (ixtiyoriy)" style={{ marginTop: 24 }}>
          <Alert
            message="Agar reys bir nechta shaharlardan o'tsa, har bir to'xtashni quyida kiriting. Bo'sh qoldirsangiz, reys faqat jo'nash → yetib borish bo'ladi."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.List name="stops">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <StopRowFields
                    key={key}
                    name={name}
                    restField={restField}
                    form={form}
                    regions={regions}
                    allCities={allCities}
                    allStations={allStations}
                    onRemove={() => remove(name)}
                  />
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  To'xtash qo'shish
                </Button>
              </>
            )}
          </Form.List>

          <SegmentPricesEditor form={form} allCities={allCities} allStations={allStations} />
        </Card>
      </Form>
    </Create>
  );
};

// --- Helpers for stop labels (used by stops + segment prices) ---

const buildStopLabel = (
  stop: { cityId?: number; stationId?: number | null } | undefined,
  index: number,
  allCities: { id: number; nameUz: string }[],
  allStations: { id: number; nameUz: string }[],
): string => {
  if (!stop || stop.cityId == null) return `#${index + 1} — (tanlanmagan)`;
  const city = allCities.find((c) => c.id === stop.cityId)?.nameUz ?? '?';
  const station = stop.stationId
    ? allStations.find((s) => s.id === stop.stationId)?.nameUz
    : undefined;
  return station
    ? `#${index + 1}. ${city} — ${station}`
    : `#${index + 1}. ${city}`;
};

/**
 * One row in the stops Form.List. Each row has its own viloyat/shahar/avtovokzal
 * dropdowns; viloyat filters shahar, shahar filters avtovokzal. We read the
 * row's own values via Form.useWatch so the dependent options update live.
 */
const StopRowFields = ({
  name,
  restField,
  form,
  regions,
  allCities,
  allStations,
  onRemove,
}: {
  name: number;
  restField: object;
  form: any;
  regions: { id: number; nameUz: string }[];
  allCities: { id: number; nameUz: string; regionId: number }[];
  allStations: { id: number; nameUz: string; cityId: number }[];
  onRemove: () => void;
}) => {
  const regionId = Form.useWatch(['stops', name, 'regionId'], form) as
    | number
    | undefined;
  const cityId = Form.useWatch(['stops', name, 'cityId'], form) as
    | number
    | undefined;

  const cityOptions = (
    regionId ? allCities.filter((c) => c.regionId === regionId) : allCities
  ).map((c) => ({ value: c.id, label: c.nameUz }));

  const stationOptions = (
    cityId ? allStations.filter((s) => s.cityId === cityId) : []
  ).map((s) => ({ value: s.id, label: s.nameUz }));

  return (
    <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
      <Col span={1} style={{ textAlign: 'center', color: '#888' }}>
        #{name + 1}
      </Col>
      <Col span={4}>
        <Form.Item
          {...restField}
          name={[name, 'regionId']}
          style={{ marginBottom: 0 }}
        >
          <Select
            allowClear
            showSearch
            placeholder="Viloyat"
            options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={() => {
              // Clear city + station when viloyat changes
              const stops = form.getFieldValue('stops') ?? [];
              stops[name] = { ...(stops[name] ?? {}), cityId: undefined, stationId: undefined };
              form.setFieldsValue({ stops });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item
          {...restField}
          name={[name, 'cityId']}
          rules={[{ required: true, message: 'Shaharni tanlang' }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            showSearch
            placeholder="Shahar"
            options={cityOptions}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={() => {
              const stops = form.getFieldValue('stops') ?? [];
              stops[name] = { ...(stops[name] ?? {}), stationId: undefined };
              form.setFieldsValue({ stops });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item
          {...restField}
          name={[name, 'stationId']}
          style={{ marginBottom: 0 }}
        >
          <Select
            allowClear
            showSearch
            placeholder={cityId ? 'Avtovokzal (ixtiyoriy)' : 'Avval shaharni tanlang'}
            disabled={!cityId}
            options={stationOptions}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={cityId ? 'Avtovokzal topilmadi' : null}
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item
          {...restField}
          name={[name, 'scheduledTime']}
          rules={[{ required: true, message: 'Vaqtni tanlang' }]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            format="YYYY-MM-DD HH:mm"
            placeholder="Sana va vaqt"
          />
        </Form.Item>
      </Col>
      <Col span={2} style={{ textAlign: 'right' }}>
        <Button
          danger
          type="text"
          icon={<MinusCircleOutlined />}
          onClick={onRemove}
        />
      </Col>
    </Row>
  );
};

/**
 * Segment prices editor. Replaces the previous "indeks" number inputs with
 * Select dropdowns showing the actual stop labels, so the operator can see
 * "Toshkent — Markaziy Vokzal → Sirdaryo" instead of "0 → 1".
 */
const SegmentPricesEditor = ({
  form,
  allCities,
  allStations,
}: {
  form: any;
  allCities: { id: number; nameUz: string; regionId: number }[];
  allStations: { id: number; nameUz: string; cityId: number }[];
}) => {
  const stops =
    (Form.useWatch('stops', form) as
      | Array<{ cityId?: number; stationId?: number | null }>
      | undefined) ?? [];

  const stopOptions = stops.map((s, i) => ({
    value: i,
    label: buildStopLabel(s, i, allCities, allStations),
  }));

  return (
    <div style={{ marginTop: 24 }}>
      <h4 style={{ marginBottom: 8 }}>Segment narxlari</h4>
      <Alert
        message="Har bir 'qaerdan → qaerga' uchun yo'lovchi to'laydigan narxni kiriting. To'xtashlar bo'lmasa, yuqoridagi standart narx ishlatiladi."
        type="warning"
        showIcon
        style={{ marginBottom: 8 }}
      />
      <Form.List name="segmentPrices">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Row gutter={8} key={key} align="middle" style={{ marginBottom: 8 }}>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'fromStopIndex']}
                    rules={[{ required: true, message: 'Qaerdan' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      placeholder="Qaerdan"
                      options={stopOptions}
                      filterOption={(input, option) =>
                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      notFoundContent={
                        stopOptions.length === 0
                          ? "Avval yuqorida to'xtashlar qo'shing"
                          : 'Topilmadi'
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'toStopIndex']}
                    rules={[{ required: true, message: 'Qaerga' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      placeholder="Qaerga"
                      options={stopOptions}
                      filterOption={(input, option) =>
                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      notFoundContent={
                        stopOptions.length === 0
                          ? "Avval yuqorida to'xtashlar qo'shing"
                          : 'Topilmadi'
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    {...restField}
                    name={[name, 'price']}
                    rules={[{ required: true, message: 'Narx' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                      parser={(v) => v!.replace(/\s/g, '') as unknown as 0}
                      placeholder="Narx (so'm)"
                    />
                  </Form.Item>
                </Col>
                <Col span={2} style={{ textAlign: 'right' }}>
                  <Button
                    danger
                    type="text"
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(name)}
                  />
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
              disabled={stopOptions.length < 2}
            >
              Segment narxi qo'shish
            </Button>
          </>
        )}
      </Form.List>
    </div>
  );
};
